import { useMutation } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { IconChevronRight } from "@tabler/icons-react";
import { HomeRecentItemDto } from "@repo/api/home";
import { paths } from "@/App";
import { Button } from "@/lib/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import Toast from "@/lib/components/Toast";
import { DfyApi } from "@/lib/api/dfy-api";
import { useTeamQuery } from "@/lib/queries/team-queries";
import { dfyQueryKeys } from "@/lib/queries/dfy-queries";
import { homeQueryKeys } from "@/lib/queries/home-queries";
import { queryClient } from "@/lib/queries/query-client";
import VideoListItem from "../Videos/VideoListItem";
import { OrderCard } from "../DoneForYou/DfyOrdersList";

export default function RecentSection({
  recent,
}: {
  recent: HomeRecentItemDto[];
}) {
  const { teamExternalId } = useParams();
  const { data: team } = useTeamQuery(teamExternalId!);

  const deleteDraftMutation = useMutation({
    mutationFn: (orderExternalId: string) =>
      DfyApi.deleteOrder(teamExternalId!, orderExternalId),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: homeQueryKeys.home(teamExternalId!),
        }),
        queryClient.invalidateQueries({
          queryKey: dfyQueryKeys.customerOrders(teamExternalId!),
        }),
      ]),
    onError: () => Toast.error("Failed to delete order"),
  });

  if (recent.length === 0) {
    return null;
  }

  const orderHref = (orderExternalId: string) =>
    team?.isDfyEditorTeam
      ? paths.dfyEditorOrder(teamExternalId!, orderExternalId)
      : paths.dfyOrder(teamExternalId!, orderExternalId);

  return (
    <section className="mt-10">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold">Recent</h2>
        <ViewAllMenu />
      </div>
      <ul className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {recent.map((item) => (
          <li
            key={
              item.type === "VIDEO"
                ? item.video.externalId
                : item.order.externalId
            }
            className="min-w-0 xl:max-2xl:nth-4:hidden"
          >
            {item.type === "VIDEO" ? (
              <VideoListItem video={item.video} />
            ) : (
              <OrderCard
                order={item.order}
                href={orderHref(item.order.externalId)}
                onDeleteDraft={
                  team?.isDfyEditorTeam
                    ? undefined
                    : (externalId) => deleteDraftMutation.mutate(externalId)
                }
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ViewAllMenu() {
  const { teamExternalId } = useParams();
  const { data: team } = useTeamQuery(teamExternalId!);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="-mr-3 gap-1 pr-3.5 text-text-secondary"
          asChild
        >
          <button type="button">
            View all
            <IconChevronRight stroke={2.5} />
          </button>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to={paths.videos(teamExternalId!)}>Videos</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          {team?.isDfyEditorTeam ? (
            <Link to={paths.dfyEditorOrders(teamExternalId!)}>Orders</Link>
          ) : (
            <Link to={paths.dfy(teamExternalId!)}>Done-for-You orders</Link>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
